import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Target, Loader2, Trash2, ExternalLink, Lightbulb,
  Shield, AlertTriangle, ChevronDown, ChevronUp, Search,
  TrendingUp, BookOpen, Hash
} from 'lucide-react';

const API = '/api';

export default function CompetitorAnalysis() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [expanded, setExpanded] = useState(null);

  const fetchIntel = async () => {
    try {
      const { data } = await axios.get(`${API}/social/competitors/intel`, { withCredentials: true });
      setItems(data.items || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchIntel(); }, []);

  const analyze = async () => {
    if (!text.trim() && !url.trim()) {
      toast.error('Paste competitor content or enter a URL');
      return;
    }
    setAnalyzing(true);
    try {
      const { data } = await axios.post(`${API}/social/competitors/analyze`, {
        competitor_name: name, content_text: text, content_url: url
      }, { withCredentials: true });
      setItems(prev => [data.intel, ...prev]);
      setName(''); setUrl(''); setText('');
      toast.success('Competitor analyzed! Insights generated.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analysis failed');
    } finally { setAnalyzing(false); }
  };

  const deleteIntel = async (id) => {
    try {
      await axios.delete(`${API}/social/competitors/${id}`, { withCredentials: true });
      setItems(prev => prev.filter(i => i.intel_id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="competitor-analysis-page">
      <div>
        <h1 className="text-xl font-bold text-slate-800">Competitor Intelligence</h1>
        <p className="text-sm text-slate-500 mt-1">Analyze competitor content to inform your AI blog and image generation</p>
      </div>

      {/* Input Form */}
      <Card className="border-indigo-100 bg-indigo-50/30">
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              placeholder="Competitor name (e.g., Kirloskar, Cummins)"
              value={name} onChange={e => setName(e.target.value)}
              data-testid="competitor-name-input"
            />
            <Input
              placeholder="URL of their post/page (optional)"
              value={url} onChange={e => setUrl(e.target.value)}
              data-testid="competitor-url-input"
            />
          </div>
          <Textarea
            placeholder="Paste competitor's post, ad copy, blog excerpt, or social media content here..."
            value={text} onChange={e => setText(e.target.value)}
            rows={4} className="resize-none"
            data-testid="competitor-content-input"
          />
          <Button onClick={analyze} disabled={analyzing || (!text.trim() && !url.trim())} data-testid="analyze-btn">
            {analyzing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing...</> : <><Search className="h-4 w-4 mr-2" /> Analyze Competitor</>}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 py-8 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading intelligence...
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12">
          <Target className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-400">No competitor analyses yet. Paste some competitor content above to get started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map(item => {
            const a = item.analysis || {};
            const isExpanded = expanded === item.intel_id;
            return (
              <Card key={item.intel_id} className="overflow-hidden" data-testid={`intel-card-${item.intel_id}`}>
                <div
                  className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setExpanded(isExpanded ? null : item.intel_id)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Target className="h-5 w-5 text-red-500 shrink-0" />
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-slate-800">{item.competitor_name}</h3>
                      <p className="text-xs text-slate-400 truncate">{item.content_summary?.slice(0, 100)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] text-slate-400">{item.created_at?.split('T')[0]}</span>
                    {item.content_url && (
                      <a href={item.content_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                        <ExternalLink className="h-3.5 w-3.5 text-slate-400 hover:text-blue-500" />
                      </a>
                    )}
                    <button onClick={e => { e.stopPropagation(); deleteIntel(item.intel_id); }} className="text-slate-300 hover:text-red-500">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                  </div>
                </div>

                {isExpanded && (
                  <CardContent className="pt-0 pb-5 space-y-4 border-t border-slate-100">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                      {/* Strengths */}
                      <div className="p-3 bg-emerald-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Shield className="h-4 w-4 text-emerald-600" />
                          <span className="text-xs font-semibold text-emerald-700">Their Strengths</span>
                        </div>
                        <p className="text-sm text-slate-600">{a.competitor_strengths || 'N/A'}</p>
                      </div>
                      {/* Weaknesses */}
                      <div className="p-3 bg-amber-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                          <span className="text-xs font-semibold text-amber-700">Their Weaknesses</span>
                        </div>
                        <p className="text-sm text-slate-600">{a.competitor_weaknesses || 'N/A'}</p>
                      </div>
                    </div>

                    {/* Our Differentiation */}
                    {a.our_differentiation_angle && (
                      <div className="p-3 bg-indigo-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="h-4 w-4 text-indigo-600" />
                          <span className="text-xs font-semibold text-indigo-700">Our Winning Angle</span>
                        </div>
                        <p className="text-sm text-slate-700 font-medium">{a.our_differentiation_angle}</p>
                      </div>
                    )}

                    {/* Keywords & Hashtags */}
                    <div className="flex flex-wrap gap-2">
                      {(a.keywords_used || []).map((kw, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>
                      ))}
                      {(a.hashtags_used || []).map((tag, i) => (
                        <Badge key={i} className="bg-blue-100 text-blue-700 text-xs"><Hash className="h-3 w-3 mr-0.5" />{tag.replace('#','')}</Badge>
                      ))}
                    </div>

                    {/* Counter Content Ideas */}
                    {a.counter_content_ideas?.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Lightbulb className="h-4 w-4 text-yellow-500" />
                          <span className="text-xs font-semibold text-slate-700">Counter Content Ideas</span>
                        </div>
                        <div className="space-y-2">
                          {a.counter_content_ideas.map((idea, i) => (
                            <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="text-[10px]">{idea.type}</Badge>
                                <span className="text-sm font-medium text-slate-700">{idea.title}</span>
                              </div>
                              <p className="text-xs text-slate-500">{idea.hook}</p>
                              {idea.why && <p className="text-xs text-emerald-600 mt-1">{idea.why}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Response Suggestions */}
                    {a.response_suggestions?.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <BookOpen className="h-4 w-4 text-violet-500" />
                          <span className="text-xs font-semibold text-slate-700">Response Suggestions</span>
                        </div>
                        {a.response_suggestions.map((s, i) => (
                          <div key={i} className="p-3 bg-violet-50 rounded-lg border border-violet-100 mb-2">
                            <p className="text-sm text-slate-600">{s}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
